import { Action, createReducer, on } from '@ngrx/store';

import { DataObject, UserObjectsResponse } from '../models/core.models';

import {
  selectObjects,
  clearSelectedObjects,
  deselectObjects,
  toggleObjectSelected,
} from '../actions/selected.actons';
import {
  objectChanged,
  massObjectOperationSuccess,
  moveObjectsSuccess,
} from '../actions/core.actions';
import { deleteWidgetsSuccess } from '../actions/widget.actions';

import { produce } from 'immer';

export interface SelectedObjectsState {
  selectedObjects: Map<string, DataObject>;
}

export const initialState: SelectedObjectsState = {
  selectedObjects: new Map<string, DataObject>(),
};

const _selectedObjectsReducer = createReducer(
  initialState,

  on(
    toggleObjectSelected,
    produce((draft, { object }) => {
      if (draft.selectedObjects.has(object.id)) {
        draft.selectedObjects.delete(object.id);
      } else {
        draft.selectedObjects.set(object.id, object);
      }
    }),
  ),

  on(
    clearSelectedObjects,
    produce((draft) => {
      draft.selectedObjects = new Map<string, DataObject>();
    }),
  ),

  // ! triggered from DashBoard "Select All"
  on(
    selectObjects,
    produce((draft, { objects }) => {
      if (!objects || objects.length === 0) {
        return;
      }

      draft.selectedObjects = new Map<string, DataObject>();

      for (const object of objects) {
        if (object.can_edit === false) {
          continue;
        }
        draft.selectedObjects.set(object.id, object);
      }
    }),
  ),

  on(
    objectChanged,
    produce((draft) => {
      draft.selectedObjects = new Map<string, DataObject>();
    }),
  ),

  on(
    deleteWidgetsSuccess,
    produce((draft, { widgetIds }) => {
      for (const id of widgetIds) {
        draft.selectedObjects.delete(id);
      }
    }),
  ),

  on(
    deselectObjects,
    produce((draft, { objectIds }) => {
      for (const id of objectIds) {
        draft.selectedObjects.delete(id);
      }
    }),
  ),

  // Any successful mass operation or move takes its objects out of the list the user
  // is looking at, so leaving them selected would have the toolbar report a selection
  // nobody can see. Dropped by id rather than cleared wholesale, so an operation run
  // from one object's card dropdown does not wipe an unrelated selection. A failure
  // keeps the selection.
  on(
    massObjectOperationSuccess,
    produce((draft, { response }) => {
      deselectResponseObjects(draft, response?.data);
    }),
  ),

  on(
    moveObjectsSuccess,
    produce((draft, { objects }) => {
      deselectResponseObjects(draft, objects?.data);
    }),
  ),
);

function deselectResponseObjects(
  draft: SelectedObjectsState,
  data: UserObjectsResponse | undefined,
) {
  if (!data) {
    return;
  }

  for (const field of ['dashboards', 'dimensions', 'feeds', 'workspaces']) {
    for (const object of data[field] ?? []) {
      draft.selectedObjects.delete(object.id);
    }
  }
}

export function selectedObjectsReducer(
  state: SelectedObjectsState,
  action: Action,
) {
  return _selectedObjectsReducer(state, action);
}
